import { useTranslation } from "react-i18next";
import { useEffect, useMemo } from "react";
import i18n from "@/locales";

// Common translations that are shared across components
export const commonTranslations = {
  en: {
    button: {
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      edit: "Edit",
      add: "Add",
      remove: "Remove",
      filter: "Filters",
      clear: "Clear filters",
      search: "Search",
    },
    loading: "Loading...",
    error: {
      email_required: "Email is required",
      password_required: "Password is required",
      firstname_required: "First name is required",
      lastname_required: "Last name is required",
      company_required: "Organization is required",
    },
  },
  fr: {
    button: {
      save: "Enregistrer",
      cancel: "Annuler",
      delete: "Supprimer",
      edit: "Modifier",
      add: "Ajouter",
      remove: "Retirer",
      filter: "Filtres",
      clear: "Effacer les filtres",
      search: "Rechercher",
    },
    loading: "Chargement...",
    error: {
      email_required: "L'email est requis",
      password_required: "Le mot de passe est requis",
      firstname_required: "Le prénom est requis",
      lastname_required: "Le nom est requis",
      company_required: "L'organisation est requise",
    },
  },
};

// Generate a unique namespace for each component based on its file path
let namespaceCounter = 0;
const namespaceMap = new Map();

function getNamespace(translations) {
  // Use a simple counter-based namespace for now
  // In production, you might want to use component name or file path
  const key = JSON.stringify(translations);
  if (!namespaceMap.has(key)) {
    namespaceMap.set(key, `component_${namespaceCounter++}`);
  }
  return namespaceMap.get(key);
}

/**
 * Custom hook for component-level translations
 * @param {Object} translations - Object with 'en' and 'fr' keys containing translation objects
 * @returns {Object} - Returns the standard i18next t function
 */
export function useComponentTranslation(translations) {
  const currentLanguage = i18n.language || "en";
  const namespace = useMemo(() => getNamespace(translations), [translations]);

  // Register translations with i18next synchronously
  useMemo(() => {
    if (!i18n.hasResourceBundle("en", namespace)) {
      // Merge with common translations
      const enTranslations = {
        ...commonTranslations.en,
        ...translations.en,
      };
      const frTranslations = {
        ...commonTranslations.fr,
        ...translations.fr,
      };

      i18n.addResourceBundle("en", namespace, enTranslations, true, true);
      i18n.addResourceBundle("fr", namespace, frTranslations, true, true);
    }
  }, [namespace, translations]);

  // Use the namespace-specific translation
  const { t } = useTranslation(namespace);

  return { t, i18n };
}

